import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SKIP_ERROR_SNACKBAR_HEADER } from '../http-error.interceptor';

export type HtsJobStatus = 'queued' | 'running' | 'needs_answer' | 'ready' | 'failed' | 'cancelled' | 'applied';

export interface HtsCreateJobRequest {
  description: string;
  facts: Record<string, unknown>;
  fobCountry?: string | null;
  licences?: unknown[];
  rfqId?: number | null;
  rfqItemId?: number | null;
  /** Product family of the stored classification facts (plan P2). */
  factFamily?: string | null;
  launchSnapshot?: { dutyRate: string | null; equation: string | null; dollarAmount: string | null };
}

export interface HtsApplyJobRequest {
  dutyRate: string;
  equation: string;
  dollarAmount: string;
  overwrite?: boolean;
}

export interface HtsJob {
  jobId: string;
  determinationId: string;
  turnIndex: number;
  status: HtsJobStatus;
  kind: string;
  description: string;
  /** Core Product Type the search used ("tin sign"); null until the first turn runs. */
  productType?: string | null;
  rfqId: number | null;
  rfqItemId: number | null;
  updatedAt: string;
  /** When the inquiry was first requested (its first turn). */
  requestedAt?: string | null;
  result: { type: 'classification' | 'question'; classification?: unknown; validation?: unknown; question?: unknown } | null;
  error: string | null;
  actualCostUsd: number | null;
}

export interface HtsJobDetail {
  determinationId: string;
  turns: Array<HtsJob & { answer: string | null }>;
}

export interface HtsJobAttentionItem {
  determinationId: string;
  description: string;
  status: 'needs_answer' | 'ready' | 'running';
  rfqId: number | null;
  rfqItemId: number | null;
  updatedAt: string;
}

export interface HtsJobsDisabled {
  enabled: false;
  code: 'jobs_disabled';
}

export type HtsJobsStatus = HtsJobsAttentionSummary | HtsJobsDisabled;

export interface HtsJobsAttentionSummary {
  needsAnswer: number;
  ready: number;
  running: number;
  items: HtsJobAttentionItem[];
}

export interface HtsJobsPage {
  items: HtsJob[];
  nextCursor: string | null;
}

@Injectable({ providedIn: 'root' })
export class HtsClassificationJobsService {
  private readonly base = `${environment.url}/api/hts-rag/jobs`;

  constructor(private readonly http: HttpClient) {}

  createJob(request: HtsCreateJobRequest): Observable<{ jobId: string; determinationId: string }> {
    // The duty dialog handles creation errors inline, including the explicit
    // disabled-queue response that starts its interactive fallback.
    return this.http.post<{ jobId: string; determinationId: string }>(this.base, request, { headers: this.headers(true) });
  }

  answer(determinationId: string, answer: string): Observable<{ jobId: string; determinationId: string }> {
    return this.http.post<{ jobId: string; determinationId: string }>(`${this.jobUrl(determinationId)}/answer`, { answer });
  }

  listJobs(options: { status?: HtsJobStatus; rfqId?: number } = {}, silent = false): Observable<HtsJob[]> {
    let params = new HttpParams();
    if (options.status) params = params.set('status', options.status);
    if (options.rfqId != null) params = params.set('rfqId', String(options.rfqId));
    return this.http.get<HtsJob[]>(this.base, { params, headers: this.headers(silent) });
  }

  /** Opt-in cursor mode; the legacy no-limit list retains its array response. */
  listJobsPage(cursor?: string, silent = false): Observable<HtsJobsPage> {
    let params = new HttpParams().set('limit', '500');
    if (cursor != null) params = params.set('cursor', cursor);
    return this.http.get<HtsJobsPage>(this.base, { params, headers: this.headers(silent) });
  }

  getAttentionSummary(silent = false): Observable<HtsJobsStatus> {
    return this.http.get<HtsJobsStatus>(`${this.base}/attention-summary`, { headers: this.headers(silent) });
  }

  getJob(determinationId: string): Observable<HtsJobDetail> {
    return this.http.get<HtsJobDetail>(this.jobUrl(determinationId));
  }

  apply(determinationId: string, request: HtsApplyJobRequest): Observable<HtsJob> {
    return this.http.post<HtsJob>(`${this.jobUrl(determinationId)}/apply`, request);
  }

  cancel(determinationId: string): Observable<HtsJob> {
    return this.http.post<HtsJob>(`${this.jobUrl(determinationId)}/cancel`, {});
  }

  retry(determinationId: string): Observable<HtsJob> {
    return this.http.post<HtsJob>(`${this.jobUrl(determinationId)}/retry`, {});
  }

  private jobUrl(determinationId: string): string {
    return `${this.base}/${encodeURIComponent(determinationId)}`;
  }

  private headers(silent: boolean): HttpHeaders {
    return silent ? new HttpHeaders().set(SKIP_ERROR_SNACKBAR_HEADER, 'true') : new HttpHeaders();
  }
}
